/* eslint-disable max-lines */

import { DynamicFormFieldModel } from 'src/app/@shared/form/dynamic-form-field/dynamic-form-field.model';
import {
  DynamicFormValues,
  FormConfig,
  FormFieldKey,
  FormFieldList, FormFieldValue,
  FormFieldsetConfig, FormFieldsetSource,
} from 'src/app/@shared/form/form.types';
import { FORM_CONFIG, FormKey } from 'src/app/@shared/form/form-config';
import { FORM_FIELDSET_CONFIG } from 'src/app/@shared/form/form-fieldset-config';
import { FormArray, FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { FormFieldModel } from 'src/app/@shared/form/form-field/form-field.model';
import { FormMainType, OrganizationType } from '@lib/types';
import { FormMenuItems } from 'src/app/@lib/types';
import {
  OrganizationContactPersonSource,
  OrganizationModel,
} from 'src/app/organization/@shared/organization.model';
import {
  OrganizationSubType,
  OrganizationSubTypeForm,
  Person,
  File as ProtoFile,
} from '@common/entities';
import { SUB_TYPE_WITH_SUB_TYPE_FORMS } from 'src/app/@shared/form/form-field-config';
import { TopicModel } from 'src/app/topic/@shared/topic.model';
import { isEmpty } from 'src/app/@shared/app-helper';

function isTopicValue(v: unknown): v is TopicModel {
  return !!v && typeof v === 'object' && 'title' in v;
}

export function createFieldsByConfig(config: FormConfig): (FormFieldModel | DynamicFormFieldModel)[] {
  if (!config) {
    return [];
  }

  return Object.keys(config).map((key: FormFieldKey) => {
    if (config[key].type === 'dynamic') {
      return new DynamicFormFieldModel(key, config[key]);
    }

    return new FormFieldModel(key, config[key]);
  });
}

export function createFormControl(_formId: FormKey, field: FormFieldModel, value?: FormFieldValue): FormControl {
  let control: FormControl;

  // eslint-disable-next-line no-undefined
  const valueIsEmpty = value === undefined || value === null;

  if (field.type === 'selectModal' || typeof value === 'object') {
    control = new FormControl<any>(valueIsEmpty
      ? null
      : value, field.validators);

    return control;
  }

  // radiogroup should always have one value selected (if not desired, use field.type = select)
  if (field.inputType === 'checkboxOrRadio' && !field.hasMultipleValues) {
    control = new FormControl<number | string>(isEmpty
      ? field.options[0]?.value || ''
      : value as (number | string), field.validators);

    return control;
  }

  switch (field.inputType) {
    case 'toggle':
      control = new FormControl<boolean | null>(valueIsEmpty
        ? false
        : !!value, field.validators);

      break;

    case 'number':
      control = new FormControl<number | null>(valueIsEmpty
        ? null
        : Number(value), field.validators);
      break;

    default:
      control = new FormControl<string | null>(valueIsEmpty
        ? null
        : value.toString(), field.validators);
  }

  return control;
}

export function createDynamicForm(formId: FormKey,
  fields: FormFieldModel[],
  values: DynamicFormValues = {},
  validators: ValidatorFn[] = []): FormGroup {
  const group: { [key in FormFieldKey]?: FormControl | FormArray } = {};

  fields.forEach((field) => {
    let control: FormControl | FormArray;

    try {
      control = createFormControl(formId, field, values[field.id]);
    } catch (error) {
      console.error(formId, field, error);
    }

    if (control) {
      group[field.id] = control;
    }
  });

  return new FormGroup(group, validators);
}

function createFormArray(_formId: FormKey, field: DynamicFormFieldModel): FormArray | null {
  let controls: FormArray = null;

  const dynamicFields = field.dynamicFieldConfig?.fields;

  if (!dynamicFields.length) {
    return null;
  }

  if (!field.isRequired) {
    return new FormArray([]);
  }

  if (dynamicFields.length === 1) {
    controls = new FormArray([createFormControl(_formId, dynamicFields[0])]);
  } else {
    // phone, contactPerson, ...
    // because our item is already required, there should be at least one input required in sub field config
    controls = new FormArray([
      createDynamicForm(_formId,
        dynamicFields,
        {},
        field.dynamicFieldConfig.groupValidators),
    ]);
  }

  return controls;
}

export function createForm(formId: FormKey, fields: (FormFieldModel | DynamicFormFieldModel)[]): FormGroup {
  const group: { [key in FormFieldKey]?: FormControl | FormArray } = {};

  fields.forEach((field) => {
    let control: FormControl | FormArray;

    try {
      if (field.type === 'dynamic') {
        control = createFormArray(formId, field as DynamicFormFieldModel);
      } else {
        control = createFormControl(formId, field);
      }
    } catch (error) {
      console.error(formId, field, error);
    }

    if (control) {
      group[field.id] = control;
    }
  });

  return new FormGroup(group);
}

// items in FormFieldList should either be FormFieldModel[] OR DynamicFormFieldModel[]
// mixing these types doesn't work so well  from a UI perspective
function getFieldListByConfig(fieldset: FormFieldsetSource, fields: FormFieldModel[] | DynamicFormFieldModel[]): FormFieldList {
  return {
    category: fieldset.category,
    id: fieldset.id || fieldset.fieldIds[0],
    items: fieldset.fieldIds
      .map((fieldId) => {
        return fields.filter((item) => {
          return item.id === fieldId;
        });
      }).flat(),
    title: fieldset.id
      ? `FORM.LIST.TITLE.${fieldset.id.toUpperCase()}`
      : '',
    type: fieldset.type || 'default',
  };
}

export function createFieldLists(_formId: FormKey, fields: (FormFieldModel | DynamicFormFieldModel)[], config?: FormFieldsetConfig): FormFieldList[] {
  if (!fields?.length) {
    return [];
  }

  if (config) {
    return config
      .map((fieldset) => {
        return getFieldListByConfig(fieldset, fields);
      })
      .filter((field) => { return !!field.items.length; });
  }

  console.warn('[fallback] default fieldList grouping because config is not set/available');

  const simpleFieldIds = fields.filter((item) => {
    return item.type !== 'dynamic';
  }).map((field) => { return field.id; });

  const dynamicFieldIds = fields.filter((item) => {
    return item.type === 'dynamic';
  }).map((field) => { return field.id; });

  return [
    getFieldListByConfig({
      fieldIds: simpleFieldIds,
    }, fields),
    getFieldListByConfig({
      fieldIds: dynamicFieldIds,
      type: 'dynamic',
    }, fields),
  ].filter((field) => { return !!field.items.length; });
}

// formSubType not set for typ7-8
// organizationSubTypeForm not set for typ5-8
export function getFormKey(formType: FormMainType,
  formSubType?: OrganizationSubType,
  organizationSubTypeForm?: OrganizationSubTypeForm): FormKey {
  // type 1a-1d, 2a-2d, 3a-3c, 4a-4c
  if (organizationSubTypeForm) {
    return organizationSubTypeForm;
  }

  if (formSubType) {
    // sub form required first, before we can receive the actual form config
    if (SUB_TYPE_WITH_SUB_TYPE_FORMS.includes(formSubType)) {
      return 'selfHelpAddress';
    }

    // typ 5,6
    // force cast here because FormKey doesn't include all formSubTypes (e.g. selfHelpAddressRa has sub forms)
    return formSubType as FormKey;
  }

  // typ 7,8, filter
  // force cast here because FormKey doesn't include all FormMainTypes (selfHelpAddress has sub types)
  return formType as FormKey;
}

export function getConfigs(formKey: FormKey): {
  formConfig: FormConfig;
  fieldsetConfig: FormFieldsetConfig;
} {
  return {
    fieldsetConfig: FORM_FIELDSET_CONFIG[formKey],
    formConfig: FORM_CONFIG[formKey],
  };
}

export function getIconByFormType(formType: FormMenuItems): string {
  let iconName: string;

  switch (formType) {
    case 'selfHelpAddress':
      iconName = 'location-outline';
      break;

    case 'otherAddress':
      iconName = 'business-outline';
      break;

    case 'person':
      iconName = 'person-outline';
      break;

    case 'user':
      iconName = 'settings-outline';
      break;

    case 'cluster':
      iconName = 'folder-outline';
      break;

    default:
      iconName = 'list-outline';
  }

  return iconName;
}

export function getColorByFormType(type: OrganizationType, subType?: OrganizationSubType): string {
  if (type === 'otherAddress') {
    return 'var(--ion-color-medium)';
  }

  let badgeColor: string;

  switch (subType) {
    case 'selfHelpAddressRa':
      badgeColor = 'var(--ion-color-danger)';
      break;

    case 'selfHelpAddressGa':
      badgeColor = 'var(--ion-color-success)';
      break;

    case 'selfHelpAddressCommunity':
      badgeColor = 'var(--ion-color-warning)';
      break;

    case 'selfHelpAddressShGroup':
      badgeColor = '#ff8000';
      break;

    case 'selfHelpAddressBa':
      badgeColor = '#3171E0';
      break;

    case 'selfHelpAddressOther':
      badgeColor = '#e7a8fe';
      break;

    default:
      badgeColor = 'var(--ion-color-medium)';
  }

  return badgeColor;
}

// always return array for easier handling; it contains 1-n items (at least an empty string)
// eslint-disable-next-line max-statements,max-lines-per-function
export function getDisplayNames(value: any, isVerbose?: boolean): string[] {
  if (!value) { return ['']; }

  const isPerson = (v: any): v is Person => { return v && typeof v === 'object' && 'firstName' in v && 'lastName' in v; };
  const isOrganization = (v: any): v is OrganizationModel => { return v && typeof v === 'object' && 'name' in v; };
  const getPersonName = (person: Person): string => {
    return [person.firstName, person.lastName]
      .map((namePart) => { return namePart?.trim(); })
      .filter(Boolean)
      .join(' ') || String((person as Person & { name?: string }).name ?? '').trim();
  };

  if (Array.isArray(value)) {
    if (value.length === 0) { return ['']; }

    const allArePersons = value.every(isPerson);
    const allAreOrgs = value.every(isOrganization);
    const allTopics = value.every(isTopicValue);

    if (allArePersons) {
      return value.map(getPersonName);
    }

    if (allAreOrgs) {
      return value.map((o: OrganizationModel) => { return o.shortName || o.name; });
    }

    if (allTopics) {
      if (!isVerbose) {
        return value.map((o) => {
          return o.title;
        });
      }

      return value.map((o) => {
        return o.verboseTitle;
      });
    }

    return value.map((v) => { return v?.toString?.() ?? ''; });
  }

  if (isPerson(value)) {
    return [getPersonName(value)];
  }

  if (isOrganization(value)) {
    return [value.shortName || value.name];
  }

  if (isTopicValue(value)) {
    if (!isVerbose) {
      return [value.title];
    }

    return [value.verboseTitle];
  }

  return [value?.toString() || ''];
}

export function getFileName(file: File | ProtoFile): string {
  if (!file) {
    return '';
  }

  // true when we already have an uploaded file
  if ('publicUrl' in file) {
    const [base] = file.name.split('_');
    const ext = file.name.substring(file.name.lastIndexOf('.'));

    return `<a href="${file.publicUrl}" target="_blank">${base + ext}</a>`;
  }

  return file.name;
}

type TopicFreeTextItem = {
  sourceTopicId?: string;
  topicName: string;
  topicSynonyms: string;
};

type ContactPersonFreeTextItem = {
  sourceContactPersonId?: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  hiddenFields: string[];
};

function getParsedTopicFreeTextItem(item: unknown): TopicFreeTextItem {
  if (typeof item === 'string') {
    return {
      topicName: item,
      topicSynonyms: '',
    };
  }

  if (isTopicValue(item)) {
    return {
      sourceTopicId: item.id || '',
      topicName: item.title || '',
      topicSynonyms: item.synonyms?.join(', ') || '',
    };
  }

  return {
    topicName: getDisplayNames(item, false)?.[0] || '',
    topicSynonyms: '',
  };
}

function getParsedContactPersons(item: OrganizationContactPersonSource): ContactPersonFreeTextItem {
  const { firstName, lastName } = item.person;

  return {
    email: item.email || '',
    firstName: firstName || '',
    hiddenFields: Array.isArray(item.hiddenFields)
      ? (item.hiddenFields as unknown[]).filter((value): value is string => {
        return typeof value === 'string';
      })
      : [],
    lastName: lastName || '',
    phoneNumber: item.phoneNumber || '',
    sourceContactPersonId: item.id || '',
  };
}

// HINT: this does not handle every form value or entity correctly but right now we only have selected free text fields
export function getFreeTextContent(originalContent: unknown,
  key: FormFieldKey): string | string[] | TopicFreeTextItem[] | ContactPersonFreeTextItem[] {
  if (isEmpty(originalContent)) {
    return null;
  }

  if (Array.isArray(originalContent)) {
    if (key === 'topics') {
      return originalContent.map((item) => {
        return getParsedTopicFreeTextItem(item);
      });
    }

    if (key === 'contactPersons') {
      return originalContent.map((item) => {
        return getParsedContactPersons(item as OrganizationContactPersonSource);
      });
    }

    return originalContent.map(() => {
      console.warn(`[OrganizationFormComponent] This key: '${key}' has no 'parse to free text' implementation yet.`);

      return '';
    }).join(', ');
  }

  if (key === 'providedByOther' || key === 'contactOrganization' || key === 'provider') {
    return getDisplayNames(originalContent, false)?.[0] || '';
  }

  return originalContent.toString();
}

export function getKeysByFormConfig<T extends Record<string, unknown>>(obj: T): (keyof T)[] {
  return Object.keys(obj) as (keyof T)[];
}
